/**
 * =============================================================================
 * SPEQ55 RFP Pro - RFP Edit Page
 * =============================================================================
 *
 * Page for editing an existing RFP. Wraps the RFPForm component with
 * edit-specific logic.
 *
 * @module pages/rfps/RFPEdit
 * @author SPEQ55
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { get, put } from '../../api/client';
import RFPForm from './RFPForm';

function RFPEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [rfp, setRfp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch RFP data.
   */
  useEffect(() => {
    const fetchRFP = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await get(`/rfps/${id}`);
        setRfp(response.data.rfp);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('RFP not found');
        } else if (err.response?.status === 403) {
          setError('You do not have permission to edit this RFP');
        } else {
          setError('Failed to load RFP');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRFP();
  }, [id]);

  /**
   * Handle form submission.
   */
  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await put(`/rfps/${id}`, formData);
      // Navigate back to RFP detail page
      navigate(`/app/rfps/${id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update RFP. Please try again.');
      setIsSubmitting(false);
    }
  };

  /**
   * Handle cancel.
   */
  const handleCancel = () => {
    navigate(`/app/rfps/${id}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-accent-500">Loading RFP...</p>
        </div>
      </div>
    );
  }

  // Error state (before form renders)
  if (error && !rfp) {
    return (
      <div className="space-y-6">
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
        <Link to="/app/rfps" className="btn-outline">
          Back to RFPs
        </Link>
      </div>
    );
  }

  if (!rfp) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Link to="/app/rfps" className="text-accent-500 hover:text-accent-700">
            RFPs
          </Link>
          <span className="text-accent-400">/</span>
          <Link to={`/app/rfps/${id}`} className="text-accent-500 hover:text-accent-700 font-mono">
            {rfp.rfpNumber}
          </Link>
          <span className="text-accent-400">/</span>
          <span className="text-accent-800">Edit</span>
        </div>
        <h1 className="text-2xl font-bold text-accent-800">Edit RFP</h1>
        <p className="mt-1 text-accent-500">Update the details for this request for proposal.</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
      )}

      {/* Form */}
      <RFPForm
        initialData={rfp}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
        submitLabel="Update RFP"
      />
    </div>
  );
}

export default RFPEdit;
